import React, { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext(null);
const KEY = "pmt_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY) || "[]");
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const CART_MERGE_KEYS = [
    "product_slug",
    "product_name",
    "design_id",
    "product_type",
    "size",
    "color",
    "quality",
    "tshirt_style",
    "printing_method",
    "custom_name",
    "custom_number",
    "custom_text",
    "notes",
    "artwork_url",
    "gift_options",
  ];

  const comparableValue = (value) =>
    value && typeof value === "object"
      ? JSON.stringify(value)
      : String(value ?? "");

  const sameCartConfiguration = (a, b) =>
    CART_MERGE_KEYS.every(
      (key) => comparableValue(a?.[key]) === comparableValue(b?.[key])
    );

  const addItem = (item) => {
    setItems((prev) => {
      const existingIndex = prev.findIndex((existing) =>
        sameCartConfiguration(existing, item)
      );

      if (existingIndex === -1) {
        return [
          ...prev,
          {
            ...item,
            _id: Date.now() + Math.random(),
            quantity: Number(item.quantity || 1),
            unit_price: Number(item.unit_price ?? item.price ?? 0),
            total_price: Number(
              (
                Number(item.unit_price ?? item.price ?? 0) *
                Number(item.quantity || 1)
              ).toFixed(0)
            ),
          },
        ];
      }

      return prev.map((existing, index) => {
        if (index !== existingIndex) return existing;

        const quantity =
          Number(existing.quantity || 1) +
          Number(item.quantity || 1);

        return {
          ...existing,
          quantity,
          unit_price: Number(
            existing.unit_price ?? existing.price ?? item.unit_price ?? item.price ?? 0
          ),
          total_price: Number(
            (
              Number(existing.unit_price ?? existing.price ?? item.unit_price ?? item.price ?? 0) *
              quantity
            ).toFixed(0)
          ),
        };
      });
    });
  };

  const removeItem = (id) => {
    setItems((prev) => prev.filter((i) => i._id !== id));
  };

  const updateItem = (id, patch) => {
    setItems((prev) =>
      prev.map((i) => {
        if (i._id !== id) return i;

        const updated = { ...i, ...patch };

        if (patch.quantity !== undefined) {
          updated.unit_price = Number(
            updated.unit_price ?? updated.price ?? 0
          );
          updated.total_price = Number(
            (
              updated.unit_price * Number(updated.quantity || 1)
            ).toFixed(0)
          );
        }

        return updated;
      })
    );
  };

  const clear = () => setItems([]);

  const count = items.reduce(
    (n, i) => n + (i.quantity || 1),
    0
  );

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateItem,
        clear,
        count,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);

  if (!ctx) {
    throw new Error("useCart must be inside CartProvider");
  }

  return ctx;
}
